import { AccessTime as AccessTimeIcon, AttachMoney as AttachMoneyIcon, CalendarMonth, People as PeopleIcon, Terrain as TerrainIcon, Timer as TimerIcon } from '@mui/icons-material'

import { InfoList } from './MiniComponent'
import { formatFullDate, formatFullDateTime } from '../../utils/TimeUtility'
import { getSportProps, getStatusProps } from '../../utils/utility'

/**
 * InfoSection - 专门用于显示课程信息的组件
 * @param {Object} props
 * @param {Object} props.info - 信息对象，包含所有可能的信息字段
 * @param {string} [props.info.title] - 标题
 * @param {string} [props.info.date] - 日期
 * @param {string} [props.info.time] - 时间
 * @param {string} [props.info.location] - 地点
 * @param {string} [props.info.sport] - 运动类型
 * @param {number} [props.info.price] - 价格
 * @param {string} [props.info.cancellationDeadline] - 取消截止时间
 * @param {string} [props.info.registrationDeadline] - 注册截止时间
 * @param {string} [props.info.status] - 状态
 * @param {number} [props.info.maxParticipants] - 最大参与人数
 * @param {number} [props.info.minParticipants] - 最小参与人数
 * @param {Array} [props.info.times] - 可选时间段
 * @param {number} [props.columns=2] - 列数
 * @param {boolean} [props.hideLabels=false] - 是否隐藏标签
 * @param {Object} [props.sx] - 额外的样式属性
 * @returns {JSX.Element}
 */
export default function InfoSection({ info = {}, columns = 2, hideLabels = false, sx = {} }) {
  const items = []

  const {
    title,
    date,
    timeSlot,
    location,
    sport,
    price,
    cancellationDeadline,
    registrationDeadline,
    status,
    maxParticipants,
    times,
    participants = [],
    showSpotsTaken = false,
    availableSpots = 0,
  } = info

  if (date) {
    const formattedDate = formatFullDate(date)
    items.push({ icon: <CalendarMonth color="action" />, label: 'Date', value: formattedDate })
  }

  if (location) {
    items.push({ icon: <TerrainIcon color="action" />, label: 'Location', value: location })
  }

  if (sport) {
    const sportProps = getSportProps(sport)
    items.push({ icon: sportProps.icon, label: 'Sport Type', value: sportProps.label })
  }

  if (times?.length > 0) {
    times.forEach((t, index) => {
      items.push({ icon: <AccessTimeIcon color="action" />, label: 'Time ' + (index + 1), value: t.time })
    })
  }

  if (timeSlot) {
    items.push({ icon: <AccessTimeIcon color="action" />, label: 'Time', value: timeSlot })
  }

  if (price) {
    items.push({ icon: <AttachMoneyIcon color="action" />, label: 'Price', value: `${price}` })
  }

  if (showSpotsTaken && participants) {
    if (maxParticipants) {
      const currentParticipants = participants?.length || 0
      items.push({
        icon: <PeopleIcon color="action" />,
        label: 'Participants',
        value: `${currentParticipants} of ${maxParticipants} spots taken`,
      })
    } else {
      const currentParticipants = participants?.length || 0
      items.push({
        icon: <PeopleIcon color="action" />,
        label: 'Participants',
        value: `${currentParticipants} people registered`,
      })
    }
  } else if (availableSpots > 0) {
    items.push({
      icon: <PeopleIcon color="action" />,
      label: 'Available spots',
      value: `Available spots: ${availableSpots}`,
    })
  }

  if (cancellationDeadline) {
    const formattedCancellationDeadline = formatFullDateTime(cancellationDeadline)
    items.push({
      icon: <TimerIcon color="action" />,
      label: 'Cancellation Deadline',
      value: formattedCancellationDeadline,
    })
  }

  if (registrationDeadline) {
    const formattedRegistrationDeadline = formatFullDateTime(registrationDeadline)
    items.push({
      icon: <TimerIcon color="action" />,
      label: 'Registration Deadline',
      value: formattedRegistrationDeadline,
    })
  }

  if (status) {
    const statusProps = getStatusProps(status)
    items.push({ icon: statusProps.icon, label: 'Status', value: statusProps.label, color: statusProps.color })
  }

  return <InfoList title={title} items={items} columns={columns} hideLabels={hideLabels} sx={sx} />
}
